import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, ExternalLink, Plus, Search, Sparkles } from "lucide-react";
import { AjoutTexteDialog, FicheDialog, RechercheDialog } from "@/components/VeilleDialogs";

const STATUTS = { a_analyser: "À analyser", a_suivre: "À suivre", pris_en_compte: "Pris en compte" };

function Fiche({ fiche }) {
  return (
    <div className="mt-2 bg-[#faf6ec] border border-[#e8dcc0] rounded-lg p-3 text-xs text-slate-700 space-y-1" data-testid="veille-fiche">
      <div className="font-semibold text-[#8a6d2f]">Fiche ZOE</div>
      <p>{fiche.resume}</p>
      {fiche.impacts_ge?.length > 0 && <div><b>Impacts GE :</b><ul className="list-disc ml-4">{fiche.impacts_ge.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
      {fiche.actions?.length > 0 && <div><b>Actions :</b><ul className="list-disc ml-4">{fiche.actions.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
      <div className="text-[10px] text-slate-400">À valider par votre conseil — généré le {fiche.generee_le?.slice(0, 10)}</div>
    </div>
  );
}

function TexteRow({ t, canEdit, onStatut, onFiche }) {
  return (
    <div className="px-5 py-3 border-b border-slate-100 last:border-0" data-testid={`veille-item-${t.id}`}>
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900">{t.titre}</div>
          <div className="text-xs text-slate-500">{t.reference}{t.theme && <> · {t.theme}</>}{t.date_echeance && <> · échéance {t.date_echeance}</>}{t.origine === "recherche" && <> · actualité officielle</>}</div>
          {t.resume && !t.fiche && <div className="text-xs text-slate-600 mt-0.5">{t.resume}</div>}
          {t.url && <a href={t.url} target="_blank" rel="noreferrer" className="text-xs text-[#17352e] inline-flex items-center gap-1 hover:underline mt-0.5"><ExternalLink className="h-3 w-3" />Source</a>}
        </div>
        {canEdit ? (
          <div className="flex items-center gap-2 shrink-0">
            <Select value={t.statut} onValueChange={(v) => onStatut(t, v)}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`veille-statut-${t.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => onFiche(t)} data-testid={`veille-fiche-btn-${t.id}`}><Sparkles className="h-3 w-3 mr-1" />Fiche ZOE</Button>
          </div>
        ) : <span className="text-xs text-slate-500 shrink-0">{STATUTS[t.statut]}</span>}
      </div>
      {t.fiche && <Fiche fiche={t.fiche} />}
    </div>
  );
}

export default function VeilleReglementaire({ canEdit }) {
  const [data, setData] = useState(null);
  const [fiche, setFiche] = useState(null);
  const [adding, setAdding] = useState(false);
  const [searching, setSearching] = useState(false);
  const load = useCallback(() => api.get("/veille").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message)), []);
  useEffect(() => { load(); }, [load]);
  const onStatut = async (t, statut) => {
    try { await api.patch(`/veille/${t.id}`, { statut }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  if (!data) return <div className="text-sm text-slate-500" data-testid="veille-loading">Chargement…</div>;
  return (
    <div className="space-y-5" data-testid="veille-panel">
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={() => setSearching(true)} data-testid="veille-recherche"><Search className="h-4 w-4 mr-1" />Rechercher les actualités officielles</Button>
        {canEdit && <Button variant="outline" onClick={() => setAdding(true)} data-testid="veille-add"><Plus className="h-4 w-4 mr-1" />Ajouter un texte</Button>}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="veille-echeances">
        <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><CalendarClock className="h-3.5 w-3.5" />Échéances réglementaires à venir</div>
        {data.echeances_a_venir.length === 0 ? <div className="text-sm text-slate-500">Aucune échéance à venir.</div> :
          data.echeances_a_venir.map((e) => <div key={e.id} className="text-sm flex gap-3"><span className="font-semibold text-[#8a6d2f] w-24">{e.date_echeance}</span>{e.titre}</div>)}
      </div>
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <h2 className="px-5 py-3 text-sm font-semibold text-slate-700 bg-slate-50 border-b border-slate-100">Textes suivis ({data.items.length})</h2>
        {data.items.map((t) => <TexteRow key={t.id} t={t} canEdit={canEdit} onStatut={onStatut} onFiche={setFiche} />)}
      </section>
      <FicheDialog texte={fiche} onClose={() => setFiche(null)} onSaved={() => { setFiche(null); load(); }} />
      <AjoutTexteDialog open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
      <RechercheDialog open={searching} canEdit={canEdit} onClose={() => setSearching(false)} onSaved={load} />
    </div>
  );
}
